// =============================================================
// 学習セッション（番号選択式ログイン）
//
// - ログイン＝「番号を選ぶ」だけ。本質的に認証ではなく識別（割り切った設計）。
// - セッションはブラウザの localStorage だけで完結させる。サーバー往復が無いので
//   ネットワークが瞬断してもログイン状態を失わない（要件）。
// - 有効期限は最終操作から SESSION_TTL_MS（3時間）。この間はリロードやタブの
//   誤操作で中断しても、同じ番号を選び直せば直前の学習状態から再開できる
//   （学習状態そのものの保存は utils/exerciseProgress.js）。
// - 明示的なログアウト（Home 右上）か有効期限切れでセッションは終わる。
// =============================================================

import { newId } from './uuid';

const SESSION_KEY = 'ex2026Session';
// 旧実装からの互換キー。正本は SESSION_KEY で、こちらは書き込みのみ行う
// （直接読むコードは残さず getCurrentUserId() を使う）。
const LEGACY_USER_ID_KEY = 'ex2026UserId';

export const SESSION_TTL_HOURS = 3;
export const SESSION_TTL_MS = SESSION_TTL_HOURS * 60 * 60 * 1000;

// lastActiveAt の書き込み頻度を抑える間隔（ログ送信のたびに書かない）
const TOUCH_INTERVAL_MS = 60 * 1000;

function readRaw() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (!Number.isFinite(parsed.userId)) return null;
    if (!Number.isFinite(parsed.lastActiveAt)) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

function writeRaw(session) {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    localStorage.setItem(LEGACY_USER_ID_KEY, String(session.userId));
  } catch (_) {
    // localStorage が使えない環境では諦める（アプリは動かし続ける）
  }
}

/** 有効なセッションを返す。期限切れなら破棄して null。 */
export function getSession() {
  const session = readRaw();
  if (!session) return null;
  if (Date.now() - session.lastActiveAt > SESSION_TTL_MS) {
    logout();
    return null;
  }
  return session;
}

/** ログイン中の学習者番号。未ログイン・期限切れなら null。 */
export function getCurrentUserId() {
  const session = getSession();
  return session ? session.userId : null;
}

export function isLoggedIn() {
  return getCurrentUserId() != null;
}

/**
 * 番号を選んでログインする。
 * attemptId＝この取り組みの ID（ログDBの attempts の主キー。番号の使い回しで前後の人の記録が
 * 混ざらないよう、ログインのたびに作る）。ただし同じ番号でログインし直した（続きから再開する）
 * ときは同じ取り組みとみなして引き継ぐ。
 */
export function loginAs(userId) {
  const now = Date.now();
  const prev = getSession();
  const resume = prev && prev.userId === userId && prev.attemptId;
  writeRaw({
    userId,
    loginAt: resume ? prev.loginAt : now,
    lastActiveAt: now,
    attemptId: resume ? prev.attemptId : newId(),
  });
  clearGuest();
}

/**
 * いまの取り組み（ログDBの attempts の1行分）。番号でログイン中ならその番号、
 * 番号なしで進めているタブなら学習者番号 0（ゲスト）。どちらでもなければ null。
 * attemptId を持たない古いセッション（この仕組みより前にログインした人）にはここで付ける。
 */
export function getCurrentAttempt() {
  const session = getSession();
  if (session) {
    if (!session.attemptId) {
      session.attemptId = newId();
      writeRaw(session);
    }
    return { attemptId: session.attemptId, learnerId: session.userId, startedAt: session.loginAt || session.lastActiveAt };
  }
  try {
    const raw = sessionStorage.getItem(GUEST_ATTEMPT_KEY);
    const guest = raw ? JSON.parse(raw) : null;
    if (guest && guest.attemptId) return { attemptId: guest.attemptId, learnerId: 0, startedAt: guest.startedAt };
  } catch (_) { /* 読めなければ取り組みなし */ }
  return null;
}

// ---- 番号なしでの演習（ゲスト）----
// 番号を選ばずに演習へ進もうとしたときは確認モーダルを出し、「そのまま進める」を選んだら
// このタブの間だけ許可する（2026-10-10変更。旧：演習は番号必須でログイン画面へ送っていた）。
// 番号なしの回答ログは学習者番号 0 で記録され、進み具合は保存されない（再開できない）。
// 取り組みの ID もタブごとに作る（同じPCで次に番号なしで始めた人と混ざらない）。
const GUEST_KEY = 'ex2026GuestOk';
const GUEST_ATTEMPT_KEY = 'ex2026GuestAttempt';

export function allowGuest() {
  try {
    sessionStorage.setItem(GUEST_KEY, '1');
    if (!sessionStorage.getItem(GUEST_ATTEMPT_KEY)) {
      sessionStorage.setItem(GUEST_ATTEMPT_KEY, JSON.stringify({ attemptId: newId(), startedAt: Date.now() }));
    }
  } catch (_) { /* 使えない環境では毎回確認する */ }
}

export function isGuestAllowed() {
  try { return sessionStorage.getItem(GUEST_KEY) === '1'; } catch (_) { return false; }
}

function clearGuest() {
  try {
    sessionStorage.removeItem(GUEST_KEY);
    sessionStorage.removeItem(GUEST_ATTEMPT_KEY);
    // 番号なしで進めていた分の進み具合・時間も消す（番号でログインし直したら番号の側で続ける）
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith('ex2026Progress:guest:') || k.startsWith('ex2026Timing:guest:'))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch (_) { /* 何もできない */ }
}

/**
 * 進み具合・所要時間を保存するときの持ち主。番号でログイン中ならその番号、
 * 番号なしで進めているタブなら 'guest:<取り組みID>'（このタブの間だけ保存する）。どちらでもなければ null。
 * 番号なしでもリロードで問1に戻らないようにするため（2026-10-12。旧：番号なしは保存しなかった）。
 */
export function getProgressOwner() {
  const userId = getCurrentUserId();
  if (userId != null) return userId;
  if (!isGuestAllowed()) return null;
  const attempt = getCurrentAttempt();
  return attempt ? `guest:${attempt.attemptId}` : null;
}

/** 持ち主ごとの保存先。番号は localStorage（ブラウザを閉じても残る）、番号なしは sessionStorage（このタブの間だけ）。 */
export function progressStorage(owner) {
  return typeof owner === 'string' && owner.startsWith('guest:') ? sessionStorage : localStorage;
}

/** 明示的なログアウト。学習状態（exerciseProgress）は番号ごとに残すので消さない。 */
export function logout() {
  try {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(LEGACY_USER_ID_KEY);
  } catch (_) {
    // 何もできない
  }
}

/** 操作があったことを記録して有効期限を延ばす（演習中にタイムアウトしないため）。 */
export function touchSession() {
  const session = readRaw();
  if (!session) return;
  const now = Date.now();
  if (now - session.lastActiveAt > SESSION_TTL_MS) {
    logout();
    return;
  }
  if (now - session.lastActiveAt < TOUCH_INTERVAL_MS) return;
  writeRaw({ ...session, lastActiveAt: now });
}

/** 残り有効時間（ミリ秒）。未ログインなら 0。 */
export function getSessionRemainingMs() {
  const session = getSession();
  if (!session) return 0;
  return Math.max(0, SESSION_TTL_MS - (Date.now() - session.lastActiveAt));
}
